import React from 'react';
import { createRoot } from 'react-dom/client';
import { ErrorFallback } from './ErrorFallback';

interface ErrorDetails {
  message: string;
  stack?: string;
  componentStack?: string;
  timestamp: string;
  url: string;
  userAgent: string;
  errorType: 'react' | 'javascript' | 'unhandled';
  errorInfo?: any;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error?: ErrorDetails;
}

export class GlobalErrorBoundary extends React.Component<
  React.PropsWithChildren<{}>,
  ErrorBoundaryState
> {
  constructor(props: React.PropsWithChildren<{}>) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    const errorDetails: ErrorDetails = {
      message: error.message,
      stack: error.stack,
      timestamp: new Date().toISOString(),
      url: window.location.href,
      userAgent: navigator.userAgent,
      errorType: 'react',
    };

    return {
      hasError: true,
      error: errorDetails,
    };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Update the error with component stack info
    const errorDetails: ErrorDetails = {
      message: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack || undefined,
      timestamp: new Date().toISOString(),
      url: window.location.href,
      userAgent: navigator.userAgent,
      errorType: 'react',
      errorInfo,
    };

    this.setState({ error: errorDetails });

    // Log to console capture if available
    const globalWindow = window as any;
    if (globalWindow.consoleCapture?.originalMethods?.error) {
      globalWindow.consoleCapture.originalMethods.error(
        'FetchDevConsole GlobalErrorBoundary caught error:',
        error,
        errorInfo
      );
    } else {
      console.error('FetchDevConsole GlobalErrorBoundary caught error:', error, errorInfo);
    }

    // Store error globally for access
    globalWindow.__FETCH_DEV_CONSOLE_ERROR__ = errorDetails;
  }

  handleReset = () => {
    this.setState({ hasError: false, error: undefined });

    // Clear global error
    const globalWindow = window as any;
    delete globalWindow.__FETCH_DEV_CONSOLE_ERROR__;
  };

  render() {
    if (this.state.hasError) {
      return <ErrorFallback error={this.state.error} onReset={this.handleReset} />;
    }

    return this.props.children;
  }
}

/**
 * Initialize global error handling for FetchDevConsole
 * This sets up handlers for both React errors and unhandled JavaScript errors
 */
export function initGlobalErrorHandling(): void {
  const globalWindow = window as any;

  // Prevent multiple initialization
  if (globalWindow.__FETCH_DEV_CONSOLE_ERROR_HANDLING__) {
    return;
  }

  globalWindow.__FETCH_DEV_CONSOLE_ERROR_HANDLING__ = true;

  // Handle unhandled JavaScript errors
  window.addEventListener('error', event => {
    const errorDetails: ErrorDetails = {
      message: event.message,
      stack: event.error?.stack,
      timestamp: new Date().toISOString(),
      url: window.location.href,
      userAgent: navigator.userAgent,
      errorType: 'javascript',
      errorInfo: {
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
        error: event.error,
      },
    };

    // Log the error
    if (globalWindow.consoleCapture?.originalMethods?.error) {
      globalWindow.consoleCapture.originalMethods.error(
        'FetchDevConsole caught unhandled JavaScript error:',
        errorDetails
      );
    }

    // Store for potential fallback page
    globalWindow.__FETCH_DEV_CONSOLE_ERROR__ = errorDetails;

    // If error is critical and no React error boundary caught it, show fallback
    if (isAppBreakingError(event.error)) {
      showErrorFallbackPage(errorDetails);
    }
  });

  // Handle unhandled promise rejections
  window.addEventListener('unhandledrejection', event => {
    const errorDetails: ErrorDetails = {
      message: event.reason?.message || 'Unhandled Promise Rejection',
      stack: event.reason?.stack,
      timestamp: new Date().toISOString(),
      url: window.location.href,
      userAgent: navigator.userAgent,
      errorType: 'unhandled',
      errorInfo: {
        reason: event.reason,
        promise: event.promise,
      },
    };

    // Log the rejection
    if (globalWindow.consoleCapture?.originalMethods?.error) {
      globalWindow.consoleCapture.originalMethods.error(
        'FetchDevConsole caught unhandled promise rejection:',
        errorDetails
      );
    }

    // Store for potential fallback page
    globalWindow.__FETCH_DEV_CONSOLE_ERROR__ = errorDetails;
  });
}

/**
 * Determine if an error is likely to break the app rendering
 */
function isAppBreakingError(error: any): boolean {
  if (!error) return false;

  const breakingPatterns = [
    /cannot read prop/i,
    /undefined is not a function/i,
    /cannot access before initialization/i,
    /maximum call stack size exceeded/i,
    /out of memory/i,
    /script error/i,
  ];

  const message = error.message || error.toString();
  return breakingPatterns.some(pattern => pattern.test(message));
}

/**
 * Show the error fallback page by taking over the entire document
 */
function showErrorFallbackPage(error: ErrorDetails): void {
  try {
    // Create a new root element
    const fallbackRoot = document.createElement('div');
    fallbackRoot.id = 'fetch-dev-console-error-root';
    fallbackRoot.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      z-index: 999999;
      background: white;
    `;

    // Clear and replace document body
    document.body.innerHTML = '';
    document.body.appendChild(fallbackRoot);

    // Render the error fallback
    const root = createRoot(fallbackRoot);
    root.render(<ErrorFallback error={error} />);
  } catch (fallbackError) {
    // If even this fails, show a basic HTML error page
    document.body.innerHTML = `
      <div style="
        font-family: system-ui, sans-serif;
        padding: 2rem;
        max-width: 800px;
        margin: 2rem auto;
        border: 1px solid #e0e0e0;
        border-radius: 8px;
        background: #fff;
      ">
        <h1 style="color: #d32f2f;">Critical Application Error</h1>
        <p>FetchDevConsole detected a critical error that prevented the application from rendering.</p>
        <details style="
          background: #f5f5f5;
          padding: 1rem;
          border-radius: 4px;
          white-space: pre-wrap;
          font-family: monospace;
          font-size: 0.875rem;
        ">
          <summary>Error Details</summary>
          ${error.message}
          
          Stack: ${error.stack || 'Not available'}
          
          Time: ${error.timestamp}
          URL: ${error.url}
        </details>
        <button onclick="window.location.reload()" style="
          background: #1976d2;
          color: white;
          border: none;
          padding: 0.75rem 1.5rem;
          border-radius: 4px;
          cursor: pointer;
          margin-top: 1rem;
        ">
          Reload Application
        </button>
        <p style="
          font-size: 0.75rem;
          color: #666;
          margin-top: 2rem;
        ">
          Error captured by FetchDevConsole v2.0
        </p>
      </div>
    `;
  }
}
